import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppStore } from '../store/useAppStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Search,
  History,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  Loader2,
  Grid3X3,
  List,
  Calendar,
  Filter,
  Trash2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { userDataApi } from '../services/data_apis';

interface ResearchSession {
  id: string;
  title: string;
  initial_query: string;
  research_length: string;
  status: string;
  current_step: string;
  progress_percentage: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  template_name?: string;
}

export const ResearchHistory: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { researches, setResearches } = useAppStore();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sessions, setSessions] = useState<ResearchSession[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<ResearchSession | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadResearchHistory();
    }
  }, [isAuthenticated]);

  const loadResearchHistory = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await userDataApi.getUserChats(50, 0, statusFilter !== 'all' ? statusFilter : undefined, token);
      setSessions(response.chats);
    } catch (err) {
      console.error('Failed to load research history:', err);
      setError('Failed to load research history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'active':
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'failed':
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'active':
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getResearchLengthLabel = (length: string) => {
    switch (length) {
      case 'quick_scan': return 'Quick Scan';
      case 'targeted': return 'Targeted';
      case 'deep_dive': return 'Deep Dive';
      default: return length;
    }
  };

  const getResearchLengthColor = (length: string) => {
    switch (length) {
      case 'quick_scan': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'targeted': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'deep_dive': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.initial_query.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.template_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleSessionClick = (session: ResearchSession) => {
    // Navigate to research page with session data
    navigate('/research', {
      state: {
        sessionId: session.id,
        query: session.initial_query,
        researchLength: session.research_length,
        resumeSession: true
      }
    });
  };

  const handleDeleteClick = (session: ResearchSession, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent session click
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sessionToDelete || !isAuthenticated) return;

    try {
      setDeleting(true);
      const token = await getAccessTokenSilently();
      await userDataApi.deleteResearchSession(sessionToDelete.id, token);
      
      // Remove from local state
      setSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
      
      // Optional: Show success message
      console.log(`Successfully deleted session: ${sessionToDelete.title || sessionToDelete.initial_query}`);
      
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete research session. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-sky-500">
              <History className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                Research History
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                View and manage your previous research sessions
              </p>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {/* Search */}
              <div className="relative flex-1 sm:w-80">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search research sessions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3X3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-sky-600 mr-3" />
            <span className="text-gray-600 dark:text-gray-400 text-lg">Loading research history...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800 dark:text-red-200">{error}</p>
            <Button
              onClick={loadResearchHistory}
              variant="outline"
              size="sm"
              className="mt-2 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Research Sessions */}
        {!loading && !error && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Total Sessions
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {sessions.length}
                      </p>
                    </div>
                    <History className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Completed
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {sessions.filter(s => s.status === 'completed').length}
                      </p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        This Week
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {sessions.filter(s => {
                          const created = new Date(s.created_at);
                          const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                          return created > weekAgo;
                        }).length}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-sky-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sessions Grid/List */}
            {filteredSessions.length === 0 ? (
              <div className="text-center py-16">
                <History className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  No research sessions found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8">
                  {searchQuery || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters.'
                    : 'Start your first research session to see it here.'
                  }
                </p>
                <Button onClick={() => navigate('/home')}>
                  Start New Research
                </Button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6'
                : 'space-y-4'
              }>
                {filteredSessions.map((session, index) => (
                  <motion.div
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    className="group"
                  >
                    <Card 
                      className="hover:shadow-lg transition-all duration-200 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                              {session.title || session.initial_query}
                            </CardTitle>
                            {session.template_name && (
                              <Badge variant="secondary" className="mt-2">
                                Template: {session.template_name}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className={getStatusColor(session.status)} variant="outline">
                              {getStatusIcon(session.status)}
                              <span className="ml-1">{session.status}</span>
                            </Badge>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSessionClick(session);
                                }}
                                title={session.status === 'completed' ? 'View Research' : 'Continue Research'}
                              >
                                <Play className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                                onClick={(e) => handleDeleteClick(session, e)}
                                title="Delete Session"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                        <CardDescription className="text-gray-600 dark:text-gray-400 line-clamp-2 mt-2">
                          {session.initial_query}
                        </CardDescription>
                      </CardHeader>
                      
                      <CardContent className="pt-0">
                        <div className="space-y-3">
                          {/* Progress bar for active sessions */}
                          {session.status === 'active' && session.progress_percentage > 0 && (
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-gray-400">
                                  {session.current_step}
                                </span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {session.progress_percentage}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                  style={{ width: `${session.progress_percentage}%` }}
                                />
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-4">
                              <Badge className={getResearchLengthColor(session.research_length)} variant="outline">
                                {getResearchLengthLabel(session.research_length)}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(session.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Research Session</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{sessionToDelete?.title || sessionToDelete?.initial_query}"?
                This action cannot be undone and will permanently remove the research session and all associated data.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleDeleteCancel} disabled={deleting}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};